import SeoHead from "@/components/SeoHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import LocalFaqSection from "./components/LocalFaqSection";

const localServices = [
  {
    icon: "ri-home-4-line",
    title: "Pose de carrelage à Sion",
    desc: "Pose de carrelage à Sion et alentours. Sols, murs, ancien ou neuf — on trouve la bonne technique pour que ça tienne.",
  },
  {
    icon: "ri-drop-line",
    title: "Rénovation salle de bain à Sion",
    desc: "On refait des salles de bain à Sion depuis des années. Démolition, plomberie, carrelage, mobilier — on coordonne le tout pour que vous n'ayez pas à gérer cinq artisans.",
  },
  {
    icon: "ri-water-flash-line",
    title: "Douche italienne à Sion",
    desc: "Douche italienne sur mesure à Sion. Receveur encastré, pente calculée, joints étanches. Le genre de travail qui ne fuit pas au bout de deux ans.",
  },
  {
    icon: "ri-layout-grid-line",
    title: "Faïence & mosaïque à Sion",
    desc: "Faïence et mosaïque à Sion. On pose des crédences de cuisine et des habillages de salle de bain qui tiennent le coup et qui ont du caractère.",
  },
  {
    icon: "ri-sun-line",
    title: "Carrelage extérieur à Sion",
    desc: "Terrasses et allées à Sion. Carrelage technique qui résiste au gel valaisan, aux UV et aux passages répétés. On ne pose pas n'importe quoi dehors.",
  },
];


const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Accueil",
      "item": "https://carreaux-bat.ch/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Sion",
      "item": "https://carreaux-bat.ch/sion"
    }
  ]
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Carrelage et Rénovation à Sion, Valais",
  "description": "Pose de carrelage, rénovation de salle de bain et douche italienne à Sion et environs. Artisan carreleur disponible dans tout le secteur.",
  "url": "https://carreaux-bat.ch/sion",
  "mainEntity": {
    "@type": "LocalBusiness",
    "name": "Carreaux-Bat Valais",
    "telephone": "+41 77 511 75 52",
    "areaServed": {
      "@type": "City",
      "name": "Sion",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Valais"
      }
    }
  }
};

export default function SionPage() {
  return (
    <div className="min-h-screen">
      <SeoHead
        title="Entreprise de Carrelage & Carreleur à Sion à Proximité | Carreaux-Bat Valais"
        description="Vous cherchez un carreleur à proximité de Sion ? Entreprise de carrelage et société de carrelage à Sion, Valais. Service de carrelage par carreleur près de chez vous à Sion, Bramois, Grimisuat, Arbaz. Pose de carrelage, douche italienne, faïence, terrasse. Devis gratuit."
        keywords="carreleur à proximité de Sion, entreprise de carrelage à proximité de Sion, société de carrelage à proximité de Sion, carreleur près de chez moi Sion, entreprise de carrelage près de chez vous Sion, service de carrelage à proximité Sion, entreprise de carrelage Sion, société de carrelage Sion, service de carrelage Sion, carreleur Sion, pose de carrelage Sion, rénovation salle de bain Sion, douche italienne Sion, carrelage Bramois, faïence Sion Valais, artisan carreleur Sion"
        canonical="https://carreaux-bat.ch/sion"
        ogTitle="Carrelage & Rénovation à Sion à Proximité — Artisan Carreleur en Valais"
        ogDescription="Vous cherchez un carreleur à proximité de Sion ? Pose de carrelage, rénovation salle de bain et douche italienne à Sion et environs. Devis gratuit sur place."
        ogImage="/images/sion-hero.jpg"
        geoPosition="46.2330;7.3606"
        geoPlacename="Sion, Valais, Suisse"
        geoRegion="CH-VS"
      schema={{
        "@context": "https://schema.org",
        "@graph": [webPageSchema, breadcrumbSchema]
      }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <img
          src="/images/sion-hero.jpg"
          alt="Carrelage Sion Valais"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block mb-4 px-4 py-1.5 bg-amber-700/80 text-white text-xs font-medium tracking-widest uppercase rounded-full">
            Chef-lieu du Valais
          </span>
          <h1 className="text-5xl font-bold text-white mb-4">Entreprise de Carrelage & Carreleur à Sion</h1>
          <p className="text-white/70 max-w-xl mx-auto text-sm leading-relaxed">
            On pose du carrelage à Sion depuis des années. Du centre-ville aux quartiers résidentiels, on connaît les spécificités des constructions locales.
          </p>
          <div className="flex items-center justify-center gap-2 mt-6 text-sm text-white/50">
            <Link to="/" className="hover:text-amber-400 transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Sion</span>
          </div>
        </div>
      </section>

      {/* Description longue */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-xs uppercase tracking-widest text-amber-700 font-medium">Sion, Valais</span>
              <h2 className="mt-2 text-3xl font-bold text-stone-900 mb-6">La capitale du Valais</h2>
              <div className="space-y-4 text-sm text-stone-500 leading-relaxed">
                <p>
                  Sion, chef-lieu du canton du Valais, est une ville d&apos;environ 35 000 habitants nichée au cœur de la plaine du Rhône, à 500 mètres d&apos;altitude. Dominée par les deux collines emblématiques de Valère et Tourbillon, Sion est l&apos;une des plus anciennes villes de Suisse, avec une histoire remontant à plus de 8 000 ans.
                </p>
                <h3 className="text-lg font-bold text-stone-800 mt-6 mb-2">Votre carreleur à Sion — Pose de carrelage et rénovation salle de bain</h3>
                <p>
                  La ville bénéficie d&apos;un ensoleillement exceptionnel — l&apos;un des plus importants de Suisse — ce qui en fait un lieu de vie particulièrement agréable. Son centre historique, ses ruelles pavées et ses nombreux monuments classés en font une destination touristique incontournable du Valais.
                </p>
                <p>
                  Sion est également un centre économique et administratif majeur, avec de nombreuses entreprises, administrations cantonales et institutions culturelles. La ville connaît un développement immobilier soutenu, avec de nombreux projets de construction et de rénovation qui font appel à nos services de carrelage.
                </p>
                <h3 className="text-lg font-bold text-stone-800 mt-6 mb-2">Entreprise de carrelage à Sion et dans les communes voisines</h3>
                <p>
                  Notre équipe intervient régulièrement à Sion et dans ses communes associées : Bramois, Salins, Grimisuat, Arbaz. Nous connaissons parfaitement les spécificités architecturales locales et les matériaux adaptés au climat valaisan, avec ses étés chauds et ses hivers rigoureux.
                </p>
                <p>
                  Que vous habitiez dans un appartement du centre-ville, une villa dans les quartiers résidentiels ou un chalet en périphérie, on adapte la pose de carrelage à votre style de vie et à votre budget. On se déplace dans tout le secteur de Sion, de Bramois à Grimisuat.
                </p>
              </div>
            </div>
            <div>
              <div className="relative rounded-lg overflow-hidden h-96 mb-6">
                <img
                  src="/images/sion-hero.jpg"
                  alt="Réalisation carrelage Sion"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-amber-50 rounded-lg p-5 border border-amber-100">
                  <p className="text-2xl font-bold text-amber-700">35 000</p>
                  <p className="text-xs text-stone-500 mt-1">Habitants à Sion</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-5 border border-stone-100">
                  <p className="text-2xl font-bold text-stone-900">500 m</p>
                  <p className="text-xs text-stone-500 mt-1">Altitude</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-5 border border-stone-100">
                  <p className="text-2xl font-bold text-stone-900">48h</p>
                  <p className="text-xs text-stone-500 mt-1">Délai d&apos;intervention</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-5 border border-amber-100">
                  <p className="text-2xl font-bold text-amber-700">Gratuit</p>
                  <p className="text-xs text-stone-500 mt-1">Devis sur place</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services locaux */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-widest text-amber-700 font-medium">Prestations locales</span>
            <h2 className="mt-3 text-3xl font-bold text-stone-900">Nos services à Sion</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {localServices.map((service) => (
              <div key={service.title} className="bg-white rounded-lg p-6 border border-stone-100 hover:border-amber-200 transition-colors">
                <div className="w-11 h-11 flex items-center justify-center bg-amber-50 rounded-lg mb-4">
                  <i className={`${service.icon} text-amber-700 text-xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-stone-900 mb-2">{service.title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed">{service.desc}</p>
              </div>
            ))}
            <div className="bg-amber-700 rounded-lg p-6 flex flex-col justify-between">
              <div>
                <h3 className="text-base font-semibold text-white mb-2">Devis gratuit à Sion</h3>
                <p className="text-sm text-amber-100 leading-relaxed">Envoyez-nous un message, on vient vous voir à Sion ou dans les environs. On regarde votre espace, on discute, et on vous dit franchement ce qu'on peut faire.</p>
              </div>
              <Link
                to="/contact"
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-white text-amber-700 text-sm font-medium rounded-full hover:bg-amber-50 transition-colors whitespace-nowrap"
              >
                Demander un devis
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-stone-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Votre projet à Sion</h2>
          <p className="text-stone-400 text-sm mb-8 max-w-xl mx-auto">
            On intervient à Sion et dans les communes voisines — Bramois, Grimisuat, Arbaz. On connaît le coin, les fournisseurs locaux, et les contraintes du climat valaisan. Envoyez-nous un message, on discute de votre projet.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-3.5 bg-amber-700 text-white font-medium rounded-full hover:bg-amber-600 transition-colors whitespace-nowrap"
            >
              Devis gratuit à Sion
            </Link>
            <a
              href="tel:+41775117552"
              className="px-8 py-3.5 border border-stone-600 text-stone-300 font-medium rounded-full hover:border-amber-600 hover:text-amber-400 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-phone-line"></i>
              +41 77 511 75 52
            </a>
          </div>
        </div>
      </section>

      <LocalFaqSection
        city="Sion"
        faqs={[
          {
            question: "Quel est le prix d'une pose de carrelage à Sion ?",
            answer: "Le prix dépend de la surface, du type de carrelage et de la préparation du support. À Sion, on intervient aussi bien dans des appartements neufs que dans des vieilles bâtisses du centre où le travail est plus complexe. On fait un devis gratuit sur place pour évaluer le chantier et vous donner un prix précis."
          },
          {
            question: "Faites-vous des devis gratuits à Sion et dans les communes voisines ?",
            answer: "Oui, les devis sont gratuits et sans engagement à Sion, Bramois, Grimisuat, Arbaz et dans tout le secteur. On vient voir votre espace, on discute de vos besoins, et on vous donne un devis détaillé sous 48h."
          },
          {
            question: "Posez-vous du carrelage dans les vieilles bâtisses du centre de Sion ?",
            answer: "C'est même notre spécialité. Les vieilles bâtisses de Sion ont des murs épais, des sols irréguliers et parfois des contraintes de patrimoine. On sait adapter la pose à ces spécificités : ragréage, découpe sur mesure, et choix de matériaux qui respectent l'esprit du lieu."
          },
          {
            question: "Travaillez-vous à Bramois et Grimisuat aussi ?",
            answer: "Oui, on intervient dans toutes les communes associées à Sion. Bramois, Grimisuat, Arbaz, Salins — on connaît les routes et les particularités de chaque quartier. Pas de frais de déplacement cachés, c'est dans notre zone d'intervention habituelle."
          },
          {
            question: "Combien de temps dure une rénovation de salle de bain à Sion ?",
            answer: "Une rénovation complète prend généralement entre 2 et 4 semaines selon la complexité. Cela inclut la démolition, la plomberie, la pose de carrelage, et l'installation sanitaire. On vous donne un planning précis avant de commencer, et on respecte les délais qu'on s'engage à tenir."
          },
          {
            question: "Quels types de carrelage recommandez-vous pour le climat valaisan ?",
            answer: "En Valais, on a des hivers rigoureux et des étés chauds. On recommande du grès cérame pour sa résistance au gel et aux variations de température. Pour l'extérieur, on privilégie les carrelages antidérapants certifiés R11 minimum. En intérieur, le choix dépend de votre style et de votre budget."
          }
        ]}
      />

      <Footer />
    </div>
  );
}
